import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { Add20Regular } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { useWindowManager, type OpenWindowConfig } from "../../windowManager/WindowManagerContext";
import { apiFetch } from "../../../utils/apiFetch";
import { fetchTeamsLite } from "../../../utils/teamsLite";
import {
    vorgangService,
    type LinkedEntityType,
    type TwvZielgruppe,
    type VorgangPickerItem,
} from "../../../services/vorgangService";
import { graphMailService } from "../../../services/graphMailService";
import { documentService } from "../../../services/documentService";
import { zeigeGlobalenHinweis } from "../../calendar/globalerHinweis";
import { vorgangNoteService } from "../../../services/vorgangNoteService";
import { noteService } from "../../../services/noteService";
import { useUserLabels } from "../../../hooks/useUserLabels";
import {
    VORGANG_ARTEN,
    DEFAULT_VORGANG_ART,
    hatAutomatischenBetreff,
} from "../../../data/vorgangArt";
import { UserMultiPicker } from "../../todo/UserMultiPicker";
import { MentionTextarea } from "../../notes/MentionTextarea";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import { mitGruppenkoepfen } from "../picker/vorgangPickerListe";
import { LinkRow } from "../detail/LinkRow";
import { contactRefFromRow } from "../detail/linkFetch";
import { TwvFristRow } from "../detail/TwvFristRow";
import { AutoGrowTextarea } from "../detail/AutoGrowTextarea";
import { resolvePeople } from "../shared/people";
import {
    EMPTY_SLOTS,
    kontaktLabel,
    type CascadeResult,
    type LinkSlots,
} from "../detail/linkSlots";
import {
    artFuerZuweisung,
    buildDraftTodoPayload,
    buildTodoInVorgangPayload,
    buildVorgangPayload,
    fristNachTwv,
    heuteIso,
    hvVorbelegung,
    kvPillText,
    resolveKv,
    todoDraftIstLeer,
    type CreateFlowState,
    type KvResolution,
    type TeamLite,
    type TodoDraftLike,
} from "./createFlowLogic";
import { BetreffFeld } from "./BetreffFeld";
import { kontaktNamensVarianten } from "../../../utils/betreffKontaktKopf";
import {
    firmenAusKontaktDetail,
    slotsMitVorgangAuf,
    statusGruppeAusName,
    vorgangStartFirmaId,
    type VorgangFirma,
    type VorgangFuerVorgabe,
} from "./vorgangFuer";
import type { KontaktRef } from "../detail/linkSlots";
import "../detail/vorgangDetail.css";
// Zuletzt: überschreibt bewusst Regeln aus vorgangDetail.css (P2, Risiko/Vertrag).
import { MailBildVorschau } from "./MailBildVorschau";
import { DokumentNameFeld, geaenderterDokumentName } from "../../documents/DokumentNameFeld";
import { teileDateiname } from "../../documents/DateinameKasten";
import "./vorgangCreate.css";
import { alleDokumente, alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Zweistufiger Vorgang-Erstell-Flow als LIGHT-Popup (Mail→Vorgang SP3):
 * schmal (≈520×640), öffnet IMMER zentriert (WindowManager `center`), ist
 * verschiebbar/minimierbar/schließbar (FloatingWindow), mobil Vollbild
 * (WindowLayer).
 *
 *  EIN Formular, keine Schritte (PO 01.09.2026). Bis dahin lag die Zuweisung
 *  auf einem eigenen ersten Schritt, hinter einem „Weiter →". Für den
 *  Normalfall — Kontakt wählen, Betreff tippen, anlegen — waren das zwei
 *  Bildschirme für eine Handvoll Felder. Der Mail-Weg (E1) war ohnehin schon
 *  einschrittig; jetzt gilt das für alle Aufrufer.
 *
 *  Oben — ZUWEISUNG (F1): „Woran möchtest du arbeiten?"
 *    → Zuweisungs-Karte mit lila Balken links: der KONTAKT + Vorgangsart.
 *    Risiko und Vertrag sind hier seit dem PO-Entscheid vom 23.08.2026 nicht
 *    mehr wählbar (P2, vorgangCreate.css) — sie werden im fertigen Vorgang
 *    nachgetragen, wo sie ohnehin hingehören. Kommt der Kontakt schon mit
 *    (Mail-Weg), entfällt dieser Teil und die Karte steht im Formular selbst.
 *  Darunter — Betreff (NUR bei Art „Sonstige" bzw. bei Zuweisung an einen
 *    bestehenden Vorgang — sonst ist der Betreff automatisch das Art-Kürzel)
 *    → „Notiz/Beschreibung" (mit @Erwähnung; wird nach dem
 *    Anlegen als ERSTE Notiz in den Aufgaben-Chat gepostet) → Beteiligte
 *    (Kontaktverantwortlichkeit als Pill FO/BO; Hauptverantwortliche als
 *    Kürzel-Chips, vorbelegt mit den Teamleitern des BO-Teams bzw. dem
 *    BO-User; Bearbeiter; zu Informierende unter „weitere Optionen",
 *    default zugeklappt) → Termine (TWV UND Frist, beide unabhängig — die
 *    frühere erstellerBeteiligt-Bedingung ist abgeschafft, C7) →
 *    „Einem bestehenden Vorgang zuweisen" ODER „Todo hinzufügen".
 *
 *  Ergebnis: ohne Vorgang-Zuweisung ein ECHTER Vorgang (+ Notiz + Inline-
 *  Todos), mit Zuweisung ein Todo im Ziel-Vorgang (+ Notiz am Todo) —
 *  Todo nie ohne Vorgang. Pure Logik (KV/Termine/Payloads): createFlowLogic.ts.
 */

export interface VorgangCreateResult {
    vorgangId?: number;
    todoId?: number;
    /**
     * K4 — der ELTERN-Vorgang, wenn der Nutzer „einem bestehenden Vorgang
     * zuweisen" gewählt hat. Es entsteht dabei nur ein Todo (`todoId`), der
     * Vorgang existierte schon.
     *
     * Bewusst ein eigenes Feld und NICHT `vorgangId`: `MailDetailWindow.onCreated`
     * verzweigt `if (result.vorgangId) … else if (result.todoId)` — ein gesetztes
     * `vorgangId` würde die Mail-Verknüpfung dort still umhängen. Ausgewertet
     * wird `parentVorgangId` nur vom Termin-Bind (`bindeAnTermin`), der seit K4
     * IMMER den Vorgang verknüpft statt des Todos.
     */
    parentVorgangId?: number;
    /**
     * T-397: neuer Name des Dokuments aus `eintragDokument` (inkl. Endung) — nur
     * gesetzt, wenn der Nutzer ihn geändert hat.
     */
    dokumentName?: string;
}

export interface OpenVorgangCreateFlowParams {
    initialContactId?: number | null;
    initialContactName?: string | null;
    /** Vorbelegter Betreff — NICHT für Mail-Aufrufer (C3), nur z. B. Schadenmappe/Austausch. */
    initialSubject?: string;
    /** Optionale Schaden-Wiedervorlage, die der aufrufende Slot vorbelegt. */
    initialTwv?: string | null;
    initialTwvZielgruppe?: TwvZielgruppe | null;
    /** Vorbelegte Notiz — NICHT für Mail-Aufrufer (C3). */
    initialNote?: string;
    /**
     * Mail→Vorgang SP5 (Entscheidung 31): Anhänge der Quell-Mail zur Übernahme
     * in den Vorgang. In Schritt 2 als abwählbare, VORAUSGEWÄHLTE Liste; nach
     * dem Anlegen werden die gewählten über `POST /mail/attachments/import`
     * als Kundendokumente (contactId + vorgangId) gespeichert.
     */
    mailAttachments?: {
        mailbox: string;
        /**
         * `inline` (T-236): ein Bild aus dem Mailtext. Es steht in einer eigenen
         * Gruppe hinter den Dateien und ist NICHT vorausgewählt — die meisten
         * eingebetteten Bilder sind Logos, die niemand im Vorgang haben will.
         */
        items: Array<{
            messageId: string;
            attachmentId: string;
            name: string;
            size?: number;
            inline?: boolean;
            /** Name des Anhangs in der Mail, wenn `name` schon ein besserer Vorschlag ist (`image001.png` → „Bild aus Mail …"). */
            originalName?: string;
        }>;
    };
    /**
     * T-397: das PDF eines Eintrags der DVAG-Kontakthistorie. Es ist noch kein
     * Dokument — der Aufrufer hängt es nach dem Anlegen an. Der Flow zeigt nur
     * seinen Namen, damit er sich vorher ändern lässt; der neue Name kommt in
     * `onCreated` als `dokumentName` zurück.
     */
    eintragDokument?: { name: string };
    initialVertrag?: { id: number; contractNbr: string; contactId?: number | null } | null;
    initialRisiko?: { id: number; name: string; contactId?: number | null } | null;
    /** Generischer Objektbezug für Typen ohne eigenen LinkRow-Slot, etwa SCHADEN. */
    linkedEntityType?: LinkedEntityType | null;
    linkedEntityId?: number | null;
    /** Nach dem Anlegen (z. B. MailLink binden). Navigation macht der Flow selbst. */
    onCreated?: (result: VorgangCreateResult) => void | Promise<void>;
    /**
     * I1: Darf in Schritt 1 „Persönlicher Vorgang" gewählt werden? Ohne Angabe
     * wird es ABGELEITET: nur, wenn der Flow OHNE vorbelegten Bezug geöffnet
     * wurde. Damit bekommt die Vorgänge-Seite die Option automatisch und die
     * Aufrufer aus Mail / Kontakt / Vertrag / Risiko / Schaden / Telefonie
     * automatisch nicht — dort gibt es immer einen Bezug.
     */
    allowPersoenlich?: boolean;
    /**
     * E1: Aus der Mail entfällt Schritt 1, sobald der Kontakt feststeht — es
     * gibt dort nichts mehr zu entscheiden, was das Formular nicht auch zeigen
     * könnte. Im Vorgangs-Modul NIE setzen: dort bleibt der Flow ausdrücklich
     * zweischrittig (PO-Vorgabe, Annahme A3).
     *
     * Ohne Kontakt greift der Parameter NICHT (E8) — dann erscheint Schritt 1
     * wie bisher, weil ein Vorgang derzeit einen Kontakt braucht.
     */
    einschrittig?: boolean;
    /**
     * E5: Betreff der Quell-Mail — NUR Vorlage für den Stift-Button neben der
     * Betreffzeile, KEINE Vorbelegung (C3 bleibt: Betreff und Inhalt der Mail
     * wandern nie automatisch in den Vorgang).
     */
    mailBetreff?: string | null;
    /**
     * PO 01.10.2026: eine schon getroffene Wahl „Vorgang für Person | Firma"
     * (aus „Kontakt zuweisen"). Gilt wie von Hand gewählt; ohne Angabe
     * entscheidet die Status-Regel (`vorgangFuer.ts`).
     */
    vorgangFuer?: VorgangFuerVorgabe;
}

/** T-39: der Entwurfs-Typ lebt bei der reinen Logik (createFlowLogic.ts). */
type TodoDraft = TodoDraftLike;

/**
 * Alles, was der Erstell-Flow von einem Ziel-Vorgang braucht.
 *
 * Bewusst NICHT `VorgangDto`: der Picker liefert seit Cluster VORGANG (D1) ein
 * schlankes DTO ohne `kunde`-Relation — für eine Auswahlliste ist die volle
 * Entity um ein Vielfaches zu viel Payload.
 */
export type ZielVorgang = { id: number; titel: string; kundeName: string | null };

const PICKER_SEITE = 20;

/**
 * Die Form des Vorgangs herstellen, ohne das Anlegen scheitern zu lassen
 * (User 03.11.2026, „nie genau ein To-do"): Der Vorgang steht schon samt
 * Inhalt; schlägt das Falten fehl, bleibt er mit einem To-do stehen und der
 * Altbestands-Lauf holt es nach.
 */
async function stelleTodoFormHerStill(vorgangId: number): Promise<void> {
    try {
        await vorgangService.stelleTodoFormHer(vorgangId);
    } catch (err) {
        console.error("[VorgangCreateFlow] To-do-Form nicht hergestellt", vorgangId, err);
    }
}

/**
 * Kleiner Portal-Picker über bestehende Vorgänge (Muster TodoDetailPageV3).
 *
 * Seit Cluster VORGANG (D1/D2) serverseitig: gesucht, sortiert und geblättert
 * wird in SQL. Vorher lud die Komponente über `getAllVorgaenge()` den GESAMTEN
 * Mandanten und filterte im Browser auf 20 Treffer — das skalierte nicht und
 * zeigte, weil dieser Weg keinerlei Sichtbarkeitsprüfung auf Datensatzebene
 * hatte, auch fremde persönliche Vorgänge.
 *
 * Geladen wird ERST beim Öffnen des Dropdowns (wie bisher) — nicht schon beim
 * Öffnen des Formulars.
 */
const VorgangZuweisenPicker: React.FC<{
    value: ZielVorgang | null;
    onChange: (v: ZielVorgang | null) => void;
    /** Kontakt des Formulars — bestimmt die Rangstufe 1 („Vorgänge dieses Kontakts"). */
    contactId?: number | null;
}> = ({ value, onChange, contactId }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const debounced = useDebouncedValue(query, 300);
    const suchbegriff = debounced.trim();
    const [items, setItems] = useState<VorgangPickerItem[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const dropRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

    const ladeSeite = useCallback(
        async (offset: number, signal?: AbortSignal) => {
            setLoading(true);
            setFehler(null);
            try {
                const { items: neu, total: t } = await vorgangService.getPickerPage({
                    contactId: contactId ?? null,
                    q: suchbegriff || undefined,
                    limit: PICKER_SEITE,
                    offset,
                    signal,
                });
                setTotal(t);
                setItems((prev) => (offset === 0 ? neu : [...prev, ...neu]));
            } catch (err) {
                if ((err as Error)?.name === "AbortError") return;
                console.error("[VorgangZuweisenPicker] Laden fehlgeschlagen", err);
                setFehler("Vorgänge konnten nicht geladen werden.");
            } finally {
                setLoading(false);
            }
        },
        [contactId, suchbegriff],
    );

    // Erste Seite beim Öffnen und bei jeder Änderung von Suchbegriff/Kontakt.
    useEffect(() => {
        if (!open) return;
        const ctrl = new AbortController();
        void ladeSeite(0, ctrl.signal);
        return () => ctrl.abort();
    }, [open, ladeSeite]);

    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            const r = btnRef.current?.getBoundingClientRect();
            if (r) setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 340) });
        };
        reposition();
        alleFenster.addEventListener("scroll", reposition, true);
        alleFenster.addEventListener("resize", reposition);
        const onDocClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t) || dropRef.current?.contains(t)) return;
            setOpen(false);
        };
        alleDokumente.addEventListener("pointerdown", onDocClick);
        return () => {
            alleFenster.removeEventListener("scroll", reposition, true);
            alleFenster.removeEventListener("resize", reposition);
            alleDokumente.removeEventListener("pointerdown", onDocClick);
        };
    }, [open]);

    const zeilen = useMemo(() => mitGruppenkoepfen(items), [items]);

    if (value) {
        return (
            <span className="vgd-value">
                <strong>{value.titel}</strong>
                <button
                    type="button"
                    className="vgd-clear"
                    aria-label="Vorgang-Zuweisung entfernen"
                    onClick={() => onChange(null)}
                >
                    ×
                </button>
            </span>
        );
    }

    return (
        <>
            <button ref={btnRef} type="button" className="vgd-pickbtn" onClick={() => setOpen((o) => !o)}>
                Vorgang auswählen…
            </button>
            {open && pos &&
                createPortal(
                    <div
                        ref={dropRef}
                        className="vgd vgd-drop"
                        style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 100000 }}
                    >
                        <input
                            className="vgd-drop-search"
                            autoFocus
                            placeholder="Vorgang, Nummer, Kunde oder Todo suchen…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {fehler ? (
                            <div className="vgd-drop-empty" style={{ color: "#b3261e" }}>{fehler}</div>
                        ) : loading && items.length === 0 ? (
                            <div className="vgd-drop-empty">Laden…</div>
                        ) : items.length === 0 ? (
                            <div className="vgd-drop-empty">Keine Treffer.</div>
                        ) : (
                            zeilen.map((zeile) =>
                                zeile.kind === "gruppe" ? (
                                    <div key={`g${zeile.schluessel}`} className="vgd-drop-head">
                                        {zeile.titel}
                                    </div>
                                ) : (
                                    <div
                                        key={zeile.item.vorgang.id}
                                        className="vgd-drop-opt"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setOpen(false);
                                            onChange({
                                                id: zeile.item.vorgang.id,
                                                titel: zeile.item.vorgang.titel,
                                                kundeName: zeile.item.vorgang.kundeName,
                                            });
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{zeile.item.vorgang.titel}</span>
                                        <span className="vgd-drop-sub">{zeile.item.vorgang.kundeName ?? "—"}</span>
                                    </div>
                                ),
                            )
                        )}
                        {total != null && items.length < total && (
                            <div className="vgd-drop-pager vgd-drop-pager--text">
                                <button
                                    type="button"
                                    disabled={loading}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        void ladeSeite(items.length);
                                    }}
                                >
                                    {loading ? "Wird geladen…" : "Mehr laden"}
                                </button>
                            </div>
                        )}
                    </div>,
                    document.body,
                )}
        </>
    );
};

const TodoDraftCard: React.FC<{
    draft: TodoDraft;
    onChange: (next: TodoDraft) => void;
    onRemove: () => void;
    /** I1/E07: im persönlichen Vorgang gibt es weder Bezüge noch Fremd-Bearbeiter. */
    persoenlich?: boolean;
}> = ({ draft, onChange, onRemove, persoenlich }) => (
    <div className="vgd-todo-row" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="vgd-field">
            <span className="vgd-label">Betreff</span>
            <input
                className="vgd-input"
                placeholder="Worum geht es? (optional)"
                value={draft.titel}
                onChange={(e) => onChange({ ...draft, titel: e.target.value })}
            />
        </div>
        <div className="vgd-field">
            <span className="vgd-label">Beschreibung / Notiz</span>
            <AutoGrowTextarea
                value={draft.beschreibung}
                onChange={(v) => onChange({ ...draft, beschreibung: v })}
                placeholder="Was soll gemacht werden?"
            />
        </div>
        {!persoenlich && (
            <LinkRow
                slots={draft.slots}
                editable
                compact
                onApply={(result: CascadeResult) => onChange({ ...draft, slots: result.slots })}
            />
        )}
        {persoenlich ? (
            // Spiegelt Annahme A5: das Backend antwortet sonst mit
            // „In einem persönlichen Vorgang bist nur du Bearbeiter."
            <div className="vgd-field">
                <span className="vgd-label">Bearbeiter</span>
                <span className="vgd-value" style={{ color: "var(--vgd-ink-3)", fontSize: "0.82rem" }}>
                    In einem persönlichen Vorgang bist nur du Bearbeiter.
                </span>
            </div>
        ) : (
            <div className="vgd-rowgrid" style={{ gridTemplateColumns: "repeat(2, minmax(160px, 1fr))" }}>
                <div className="vgd-field">
                    <span className="vgd-label">Bearbeiter</span>
                    <UserMultiPicker value={draft.bearbeiter} onChange={(ids) => onChange({ ...draft, bearbeiter: ids })} />
                </div>
                <div className="vgd-field">
                    <span className="vgd-label">Zu informieren</span>
                    <UserMultiPicker
                        value={draft.zuInformierende}
                        onChange={(ids) => onChange({ ...draft, zuInformierende: ids })}
                    />
                </div>
            </div>
        )}
        <TwvFristRow
            twv={draft.twv}
            frist={draft.frist}
            editable
            onChange={(p) =>
                onChange({ ...draft, ...(p.twv !== undefined ? { twv: p.twv } : {}), ...(p.frist !== undefined ? { frist: p.frist } : {}) })
            }
        />
        <button type="button" className="vgd-clear" style={{ alignSelf: "flex-end", fontSize: "0.82rem" }} onClick={onRemove}>
            Todo entfernen ×
        </button>
    </div>
);

/** Anhang-Größe menschenlesbar (identisch zur Mail-Anzeige). */
const formatBytes = (bytes?: number): string => {
    if (!bytes || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Zerlegt einen Anhang-Dateinamen in Basename OHNE Extension und Extension
 *  (inkl. Punkt) — für das editierbare Namensfeld (analog zum Umbenennen-Dialog
 *  in DocumentsSection). Ein führender Punkt zählt NICHT als Extension. */
const splitAttName = (fileName: string): { base: string; ext: string } => {
    const i = fileName.lastIndexOf(".");
    return i > 0 && i < fileName.length - 1
        ? { base: fileName.slice(0, i), ext: fileName.slice(i) }
        : { base: fileName, ext: "" };
};

const VorgangCreateFlowWindow: React.FC<
    OpenVorgangCreateFlowParams & { windowId: string }
> = ({ windowId, initialContactId, initialContactName, initialSubject, initialTwv, initialTwvZielgruppe, initialNote, mailAttachments, eintragDokument, onCreated, initialVertrag, initialRisiko, linkedEntityType, linkedEntityId, allowPersoenlich, einschrittig, mailBetreff, vorgangFuer: vorgangFuerVorgabe }) => {
    const navigate = useNavigate();
    const { closeWindow } = useWindowManager();
    const { user } = useAuth();
    const currentUserId: string | null = user?.id ?? null;
    const { users, label } = useUserLabels();

    /**
     * Steht der Kontakt schon fest (Mail-Weg, E1)? Dann entfällt die
     * Zuweisungs-Auswahl ganz und die Karte steht stattdessen oben im Formular.
     *
     * Seit dem 01.09.2026 gibt es KEINE zwei Schritte mehr — alle Aufrufer
     * sehen ein Formular. Der Name bleibt, weil er die Frage beantwortet, die
     * an den Verzweigungen unten tatsächlich gestellt wird: „kam der Kontakt
     * schon mit?"
     *
     * EINMAL beim Mount ausgewertet — mitten im Formular umzuschalten wäre für
     * den Nutzer ein Sprung. Greift nur MIT Kontakt: `POST /vorgang` weist
     * einen Vorgang ohne Kunden ab (Annahme A10; der kontaktlose Vorgang ist
     * I2).
     */
    const startetEinschrittig =
        !!einschrittig &&
        !!(initialContactId ?? initialVertrag?.contactId ?? initialRisiko?.contactId);
    const [slots, setSlots] = useState<LinkSlots>(() => {
        const base = { ...EMPTY_SLOTS };
        const cid = initialContactId ?? initialVertrag?.contactId ?? initialRisiko?.contactId ?? null;
        if (cid) {
            base.kontakt = { id: cid, lastname: (cid === initialContactId ? initialContactName : null) ?? `Kontakt ${cid}` };
        }
        if (initialVertrag) {
            base.vertrag = { id: initialVertrag.id, contractNbr: initialVertrag.contractNbr };
            base.vertraege = [base.vertrag];
        }
        if (initialRisiko) {
            base.risiko = { id: initialRisiko.id, name: initialRisiko.name };
            base.risiken = [base.risiko];
        }
        return base;
    });
    /**
     * PO 01.10.2026 („Wenn ich auf ‚Neuer Vorgang' gehe, dann soll er auch mit
     * der Firma starten (wenn Sonstige); wenn Kunde oder Interessent, dann
     * Person — ich kann es ja ändern."): Ist der gewählte Kontakt eine Person
     * mit Firmenbezug, steht unter der Zuweisung „Vorgang für: <Person> |
     * <Firma>". Vorbelegt nach derselben Regel wie „Kontakt zuweisen"
     * (`vorgangStartetMitFirma`); auf die Firma gelegt, bleibt die Person als
     * weiterer Kontakt beteiligt.
     *
     * Wie beim Hauptverantwortlichen bleibt eine Wahl VON HAND über einen
     * Kontaktwechsel hinweg stehen — sonst gilt für jeden neuen Kontakt
     * wieder die Regel. Eine Vorgabe des Aufrufers zählt als von Hand.
     */
    const [vorgangFuer, setVorgangFuer] = useState<{
        person: KontaktRef;
        firmen: VorgangFirma[];
        /** null = der Vorgang läuft auf die Person. */
        aufFirmaId: number | null;
    } | null>(null);
    const vorgangFuerRef = useRef(vorgangFuer);
    vorgangFuerRef.current = vorgangFuer;
    const vorgangFuerVonHand = useRef<"person" | "firma" | null>(vorgangFuerVorgabe?.auf ?? null);
    const wunschFirma = useRef(vorgangFuerVorgabe?.firma ?? null);
    const slotsRef = useRef(slots);
    slotsRef.current = slots;

    /**
     * Die Art ist vorbelegt („Sonstige"), kein Zwang zur Auswahl — und sie
     * BLEIBT es, auch wenn aus der Mail ein Kontakt erkannt wurde (PO
     * 23.08.2026: „wenn ein Kunde erkannt wird, dann default Vorgangsart:
     * Sonstige"). Die Status-Vorauswahl aus E3 (Interessent → NKG, Kunde → GAB)
     * ist damit zurückgenommen: sie schloss vom Kontaktstatus auf die Absicht
     * der Mail, und eine falsche Vorbelegung kostet den Nutzer mehr, als eine
     * fehlende ihm spart. Geändert wird die Art weiterhin im Select darunter.
     */
    const [vorgangsart, setVorgangsart] = useState<string>(DEFAULT_VORGANG_ART);

    /**
     * I1/E07: „Persönlicher Vorgang" steht nur zur Wahl, wenn der Flow ohne
     * vorbelegten Bezug geöffnet wurde (Vorgänge-Seite → „+ Vorgang"). Aus
     * Kontakt/Vertrag/Risiko/Schaden heraus gibt es immer einen Bezug — dort
     * wäre die Option fachlich falsch und würde serverseitig abgelehnt.
     *
     * Der Mail-Weg entscheidet es seit PO 14.09.2026 selbst und gibt
     * `allowPersoenlich` ausdrücklich mit: ohne erkannten Kunden ja, mit nein.
     * Die Ableitung hier greift für ihn deshalb nicht mehr.
     */
    const persoenlichMoeglich =
        allowPersoenlich ??
        (!initialContactId && !initialVertrag && !initialRisiko && !linkedEntityType && !mailAttachments);
    const [persoenlich, setPersoenlich] = useState(false);

    const [betreff, setBetreff] = useState(initialSubject ?? "");
    // T-46: Namen der zugewiesenen Kontakte — nur noch für das Courté-Schema
    // („Name - VSNR …"); bis zum ersten „/" schneidet das BetreffFeld seit
    // 01.10.2026 immer, auch ohne Kontakt. Hängt an den AKTUELLEN
    // Slots, damit es auch nach „ändern" stimmt.
    const kontaktNamen = useMemo(() => {
        if (persoenlich) return [];
        const alle = [slots.kontakt, ...(slots.kontakte ?? [])].flatMap((k) => kontaktNamensVarianten(k));
        return [...new Set(alle)];
    }, [persoenlich, slots.kontakt, slots.kontakte]);
    const [notiz, setNotiz] = useState<{ text: string; mentions: string[] }>({
        text: initialNote ?? "",
        mentions: [],
    });
    /**
     * PO 28.09.2026: „Hauptverantwortlich bin ich, wenn keiner vorbelegt ist."
     * Startwert ist der Anlegende; die Kontaktverantwortlichkeit ersetzt ihn,
     * sobald sie aufgelöst ist — aber nur, solange niemand von Hand gewählt hat.
     */
    const [hv, setHvRoh] = useState<string[]>(() => hvVorbelegung([], currentUserId));
    const hvVonHand = useRef(false);
    const setHv = (ids: string[]) => {
        hvVonHand.current = true;
        setHvRoh(ids);
    };
    // D1: „weiterer Bearbeiter" (nicht „Mitarbeiter"); Payload-Feld bleibt `bearbeiter`.
    const [bearbeiter, setBearbeiter] = useState<string[]>([]);
    const [zuInformierende, setZuInformierende] = useState<string[]>([]);
    /**
     * PO 28.09.2026: „TWV vorbelegt auf heute" und „TWV und Frist gleichsetzen".
     * Die Frist folgt der TWV, bis sie jemand bewusst ändert (`fristNachTwv`).
     */
    const [twv, setTwvRoh] = useState<string | null>(() => initialTwv ?? heuteIso());
    const [frist, setFristRoh] = useState<string | null>(() => initialTwv ?? heuteIso());
    const fristVonHand = useRef(false);
    const setTwv = (neu: string | null) => {
        setTwvRoh(neu);
        setFristRoh((alt) => fristNachTwv(neu, alt, fristVonHand.current));
    };
    const setFrist = (neu: string | null) => {
        fristVonHand.current = true;
        setFristRoh(neu);
    };
    const [targetVorgang, setTargetVorgang] = useState<ZielVorgang | null>(null);
    const [todoDrafts, setTodoDrafts] = useState<TodoDraft[]>([]);
    const draftKey = useRef(0);
    const [kv, setKv] = useState<KvResolution | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // SP5 (Entscheidung 31): Anhänge der Mail sind VORAUSGEWÄHLT, aber abwählbar.
    const attKey = (a: { messageId: string; attachmentId: string }) => `${a.messageId}:${a.attachmentId}`;
    const [selectedAttKeys, setSelectedAttKeys] = useState<Set<string>>(
        () => new Set((mailAttachments?.items ?? []).filter((a) => !a.inline).map(attKey)),
    );
    const toggleAtt = (key: string) =>
        setSelectedAttKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    /**
     * PO 14.09.2026: „beim neuer vorgang auch schon dokumente hochladen".
     * Die Dateien werden ERST nach dem Anlegen hochgeladen — vorher gibt es
     * keine Vorgangs-/Todo-Id, an der sie haengen koennten.
     */
    const [dateien, setDateien] = useState<File[]>([]);
    const dateiInputRef = useRef<HTMLInputElement | null>(null);
    const dateienWaehlen = (liste: FileList | null) => {
        if (!liste?.length) return;
        // ERST kopieren, DANN zurücksetzen (PO 28.09.2026: „upload docs zu neuen
        // Vorgang fix!!!"). `input.files` ist eine LEBENDE Liste: Das
        // Zurücksetzen von `value` darunter leert sie sofort. Die Kopie stand
        // vorher INNERHALB des setState-Updaters — und den ruft React erst beim
        // nächsten Rendern auf, also NACH dem Zurücksetzen. Die gewählten Dateien
        // kamen so als leere Liste an: nichts erschien, nichts wurde hochgeladen.
        const neu = Array.from(liste);
        // Anhaengen statt ersetzen: wer zweimal waehlt, meint beide Stapel.
        setDateien((prev) => [...prev, ...neu]);
        // Zuruecksetzen, damit dieselbe Datei erneut gewaehlt werden kann.
        if (dateiInputRef.current) dateiInputRef.current.value = "";
    };

    // SP6: editierbarer Basename je Anhang (Extension bleibt fixer Suffix).
    // Vorbefüllt mit dem Graph-Originalnamen OHNE Extension; unveränderte Namen
    // werden beim Import NICHT mitgesendet (Backend nimmt dann den Graph-Namen).
    const [attNames, setAttNames] = useState<Record<string, string>>(
        () =>
            Object.fromEntries(
                (mailAttachments?.items ?? []).map((a) => [attKey(a), splitAttName(a.name).base]),
            ),
    );
    const setAttName = (key: string, value: string) =>
        setAttNames((prev) => ({ ...prev, [key]: value }));

    // T-397: Name des Kontakthistorie-PDFs; bearbeitet wird nur der Teil vor der Endung.
    const [eintragDokumentStamm, setEintragDokumentStamm] = useState(
        () => teileDateiname(eintragDokument?.name ?? "").stamm,
    );
    const geaenderterEintragDokumentName = (): string | undefined =>
        eintragDokument ? geaenderterDokumentName(eintragDokument.name, eintragDokumentStamm) : undefined;

    // Kontaktverantwortlichkeit (jetzt FO + BO, C5) auflösen + Hauptverant-
    // wortlichen vorbelegen: Teamleiter des BO-Teams (alle) bzw. der BO-User.
    useEffect(() => {
        const kontaktId = slots.kontakt?.id;
        if (!kontaktId) {
            setKv(null);
            // Ohne Kontakt gibt es keine Vorbelegung — dann bin ich es (PO 28.09.2026).
            if (!hvVonHand.current && !persoenlich) setHvRoh(hvVorbelegung([], currentUserId));
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const [{ data: contact }, teams] = await Promise.all([
                    apiFetch<any>(`contact/${kontaktId}`),
                    fetchTeamsLite() as Promise<TeamLite[]>,
                ]);
                if (cancelled) return;
                const resolved = resolveKv(contact ?? {}, teams);
                setKv(resolved);
                setSlots((prev) => {
                    if (!prev.kontakt || prev.kontakt.id !== kontaktId) return prev;
                    const ref = contactRefFromRow(contact ?? {});
                    const istPlatzhalter =
                        !prev.kontakt.lastname || prev.kontakt.lastname.startsWith("Kontakt ");
                    const displayName =
                        contact.displayName ||
                        [contact.lastname, contact.firstName].filter(Boolean).join(", ") ||
                        `Kontakt ${kontaktId}`;
                    return {
                        ...prev,
                        kontakt: {
                            ...prev.kontakt,
                            // Name: unverändertes Verhalten — nur ein Platzhalter wird ersetzt.
                            lastname: istPlatzhalter ? displayName : prev.kontakt.lastname,
                            typ: prev.kontakt.typ ?? ref.typ ?? null,
                            // NEU: Anschrift (Kopfkarte des Mail-Wegs) und Kontaktstatus
                            // (E3, Art-Vorauswahl) IMMER nachziehen. Der frühere
                            // „Name ist kein Platzhalter"-Kurzschluss ließ beides auf dem
                            // Mail-Weg nie ankommen — dort steht der Name schon.
                            adresse: ref.adresse ?? prev.kontakt.adresse ?? null,
                            kundeStatus: ref.kundeStatus ?? prev.kontakt.kundeStatus ?? null,
                        },
                    };
                });
                // Von Hand Gewähltes bleibt; sonst die KV-Vorbelegung, ohne sie ich.
                if (!hvVonHand.current) setHvRoh(hvVorbelegung(resolved.hvPrefill, currentUserId));
            } catch (err) {
                console.error("[VorgangCreateFlow] KV-Auflösung fehlgeschlagen", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slots.kontakt?.id]);

    // PO 01.10.2026: Person mit Firmenbezug? Dann „Vorgang für" anbieten und
    // nach der Regel (bzw. der Wahl von Hand) auf die Firma legen.
    useEffect(() => {
        const kontaktId = slots.kontakt?.id;
        const wahl = vorgangFuerRef.current;
        // Der Wechsel kam aus dem Umschalter (oder der Mehrfachauswahl) zwischen
        // Person und ihren Firmen — dann nur die Anzeige nachziehen.
        if (wahl && kontaktId != null && (kontaktId === wahl.person.id || wahl.firmen.some((f) => f.id === kontaktId))) {
            const aufFirmaId = kontaktId === wahl.person.id ? null : kontaktId;
            if (aufFirmaId !== wahl.aufFirmaId) setVorgangFuer({ ...wahl, aufFirmaId });
            return;
        }
        setVorgangFuer(null);
        if (!kontaktId || persoenlich) return;
        let cancelled = false;
        (async () => {
            try {
                const { data: detail } = await apiFetch<any>(`contact/${kontaktId}`, {
                    params: { include: "relations" },
                });
                if (cancelled) return;
                const firmen = firmenAusKontaktDetail(detail);
                // Die im Popup gewählte Firma kennt der Flow auch dann, wenn die
                // Beziehung im Detail (noch) fehlt — sie geht nicht verloren.
                const wunsch = wunschFirma.current;
                if (wunsch && String(detail?.type?.type ?? "") === "Person" && !firmen.some((f) => f.id === wunsch.id)) {
                    firmen.push({ id: wunsch.id, name: wunsch.displayName });
                }
                if (firmen.length === 0) return;
                const aktuell = slotsRef.current;
                if (aktuell.kontakt?.id !== kontaktId) return;
                const person: KontaktRef = {
                    ...aktuell.kontakt,
                    ...contactRefFromRow(detail ?? {}),
                    id: kontaktId,
                };
                let aufFirmaId = vorgangStartFirmaId({
                    statusGruppe: statusGruppeAusName(detail?.status?.status),
                    firmen,
                    vonHand: vorgangFuerVonHand.current,
                    wunschFirmaId: wunsch?.id ?? null,
                });
                // Ein mitgebrachter Vertrag/Risiko gehört der Person — dann nicht
                // still umlegen, außer es wurde ausdrücklich die Firma gewählt.
                if (
                    aufFirmaId != null &&
                    vorgangFuerVonHand.current !== "firma" &&
                    (aktuell.risiken.length > 0 || aktuell.vertraege.length > 0)
                ) {
                    aufFirmaId = null;
                }
                const neu = { person, firmen, aufFirmaId };
                vorgangFuerRef.current = neu;
                setVorgangFuer(neu);
                if (aufFirmaId != null) {
                    const firma = firmen.find((f) => f.id === aufFirmaId)!;
                    setSlots((prev) =>
                        prev.kontakt?.id !== kontaktId
                            ? prev
                            : slotsMitVorgangAuf(
                                  prev,
                                  person,
                                  { id: firma.id, lastname: firma.name, typ: "Firma" },
                                  firmen.map((f) => f.id),
                              ),
                    );
                }
            } catch (err) {
                console.error("[VorgangCreateFlow] Firmenbezug konnte nicht geladen werden", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slots.kontakt?.id, persoenlich]);

    /** „Vorgang für" von Hand umschalten: null = Person, sonst die Firma. */
    const waehleVorgangFuer = (aufFirmaId: number | null) => {
        const wahl = vorgangFuerRef.current;
        if (!wahl) return;
        vorgangFuerVonHand.current = aufFirmaId == null ? "person" : "firma";
        const firma = aufFirmaId != null ? wahl.firmen.find((f) => f.id === aufFirmaId) : undefined;
        const neu = { ...wahl, aufFirmaId: firma ? firma.id : null };
        vorgangFuerRef.current = neu;
        setVorgangFuer(neu);
        setSlots((prev) =>
            slotsMitVorgangAuf(
                prev,
                wahl.person,
                firma ? { id: firma.id, lastname: firma.name, typ: "Firma" } : null,
                wahl.firmen.map((f) => f.id),
            ),
        );
    };

    const vorgangFuerBlock =
        vorgangFuer && !persoenlich ? (
            <div className="vgd-field" role="radiogroup" aria-label="Vorgang für">
                <span className="vgd-label">Vorgang für</span>
                <label className="vgd-radio">
                    <input
                        type="radio"
                        name="vgd-vorgang-fuer"
                        checked={vorgangFuer.aufFirmaId == null}
                        onChange={() => waehleVorgangFuer(null)}
                    />
                    <span>{kontaktLabel(vorgangFuer.person)}</span>
                    <span className="vgd-radio-hint">Person</span>
                </label>
                {vorgangFuer.firmen.map((f) => (
                    <label key={f.id} className="vgd-radio">
                        <input
                            type="radio"
                            name="vgd-vorgang-fuer"
                            checked={vorgangFuer.aufFirmaId === f.id}
                            onChange={() => waehleVorgangFuer(f.id)}
                        />
                        <span>{f.name}</span>
                        <span className="vgd-radio-hint">Firma</span>
                    </label>
                ))}
                {vorgangFuer.aufFirmaId != null && (
                    <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", marginTop: 4 }}>
                        {kontaktLabel(vorgangFuer.person)} bleibt als Kontakt am Vorgang beteiligt.
                    </div>
                )}
            </div>
        ) : null;

    // KMVS-Kürzel für die KV-Pill (Muster vorgang/shared/people.ts);
    // Fallback auf den Anzeigenamen, solange die Userliste noch lädt.
    const kuerzel = useCallback(
        (userId: string): string => resolvePeople([userId], users)[0]?.initials ?? label(userId),
        [users, label],
    );
    const kvText = kv ? kvPillText(kv, kuerzel) : "—";

    /**
     * Die Art, die WIRKLICH gilt — bei „persönlich" immer „Sonstige"
     * (PO 04.09.2026). Ab hier liest sie JEDER Verbraucher: Validierung,
     * Betreff-Pflicht, Payload, Select und Platzhalter. Ein halber Umbau
     * erzeugte eine Anzeige, die nicht dem gesendeten Payload entspricht.
     */
    const effektiveArt = artFuerZuweisung(persoenlich, vorgangsart);

    /**
     * I1: Umschalten zwischen „Vorgang für einen Kontakt" und „Persönlicher
     * Vorgang". Beim Wechsel auf persönlich werden die Bezüge VERWORFEN (kein
     * unsichtbarer Rest, der beim Speichern doch mitfährt) und die Beteiligten
     * auf den eigenen Nutzer festgelegt — das spiegelt die Backend-Regel aus
     * `setBeteiligte` („ein persönlicher Vorgang hat keine weiteren
     * Beteiligten"). Zurück gilt wieder die KV-Vorbelegung, sobald ein Kontakt
     * gewählt ist.
     */
    const wechsleZuweisungsart = (naechsteIstPersoenlich: boolean) => {
        setPersoenlich(naechsteIstPersoenlich);
        if (naechsteIstPersoenlich) {
            setSlots({ ...EMPTY_SLOTS });
            setKv(null);
            setHvRoh(currentUserId ? [currentUserId] : []);
            setBearbeiter(currentUserId ? [currentUserId] : []);
            setZuInformierende([]);
            setTargetVorgang(null);
        } else {
            // Zurück auf „für einen Kontakt": wieder ich als HV, bis ein
            // Kontakt seine Vorbelegung mitbringt.
            hvVonHand.current = false;
            setHvRoh(hvVorbelegung([], currentUserId));
            setBearbeiter([]);
        }
    };
    /**
     * PO 28.09.2026 („Schnellanlage … ohne Betreff … keine Restriktionen, also
     * kein ‚ich muss etwas ausfüllen um anzulegen'"): Es gibt KEINE Pflicht-
     * felder mehr. Bis dahin waren Kontakt, Betreff (bei „Sonstige" und beim
     * Zuweisen) und ein Hauptverantwortlicher Pflicht, und der Knopf blieb
     * grau. Ohne Betreff trägt NKG/JG/GAB weiter das Kürzel (PO 02.08.2026).
     */
    const betreffOptional = hatAutomatischenBetreff(effektiveArt) && !targetVorgang;

    const addDraft = () =>
        setTodoDrafts((prev) => [
            ...prev,
            {
                key: draftKey.current++,
                titel: "",
                beschreibung: "",
                slots: persoenlich ? { ...EMPTY_SLOTS } : slots,
                bearbeiter: currentUserId ? [currentUserId] : [],
                zuInformierende: [],
                twv: null,
                frist: null,
            },
        ]);

    // SP5: gewählte Mail-Anhänge nach dem Anlegen als Kundendokumente ablegen
    // (contactId + vorgangId). Best-effort — ein Import-Fehler darf die
    // erfolgreiche Vorgang-Anlage nicht kippen (nur Log).
    const importSelectedAttachments = async (contactId: number | null, vorgangId: number) => {
        if (!mailAttachments || selectedAttKeys.size === 0) return;
        const attachments = mailAttachments.items
            .filter((a) => selectedAttKeys.has(attKey(a)))
            .map((a) => {
                const key = attKey(a);
                const origBase = splitAttName(a.name).base;
                const editedBase = (attNames[key] ?? origBase).trim();
                // Nur einen abweichenden, nicht-leeren Namen mitsenden — sonst
                // undefined → Backend übernimmt den Graph-Originalnamen.
                const graphBase = splitAttName(a.originalName ?? a.name).base;
                const name = editedBase && editedBase !== graphBase ? editedBase : undefined;
                return { messageId: a.messageId, attachmentId: a.attachmentId, name };
            });
        if (attachments.length === 0) return;
        try {
            await graphMailService.importAttachments({
                mailbox: mailAttachments.mailbox,
                // Ohne Kunden (persönlicher Vorgang) ist der Vorgang das
                // Ablageziel — der Server prüft dann ihn statt des Kontakts.
                contactId: contactId ?? undefined,
                vorgangId,
                attachments,
            });
        } catch (err) {
            console.error("[VorgangCreateFlow] Anhang-Import fehlgeschlagen", err);
        }
    };

    /**
     * Ausgewaehlte Dateien an das eben Angelegte haengen. Wie der Anhang-Import
     * best effort: der Vorgang ist zu diesem Zeitpunkt bereits da, ein
     * fehlgeschlagener Upload darf ihn nicht als gescheitert erscheinen lassen.
     * Anders als dort bleibt es aber nicht beim Konsolen-Log — was NICHT
     * ankommt, sagt ein globaler Hinweis, sonst faende der Nutzer sein
     * Dokument spaeter einfach nicht.
     *
     * Mit Kontakt wandert das Dokument zugleich in die Kundenakte; ohne
     * (persoenlicher Vorgang) haengt es nur am Vorgang — `POST /documents`
     * verlangt nur EINEN Bezug.
     */
    const ladeDateienHoch = async (ziel: { vorgangId?: number; todoId?: number }) => {
        if (dateien.length === 0) return;
        const gescheitert: string[] = [];
        for (const file of dateien) {
            try {
                await documentService.upload({
                    file,
                    ...ziel,
                    ...(slots.kontakt ? { contactId: slots.kontakt.id } : {}),
                });
            } catch (err) {
                console.error("[VorgangCreateFlow] Datei-Upload fehlgeschlagen", file.name, err);
                gescheitert.push(file.name);
            }
        }
        if (gescheitert.length > 0) {
            zeigeGlobalenHinweis(
                `Nicht hochgeladen: ${gescheitert.join(", ")}. Der Vorgang wurde angelegt — die Datei lässt sich dort erneut hinzufügen.`,
                { art: "fehler" },
            );
        }
    };

    const save = useCallback(async () => {
        // PO 28.09.2026: keine Pflichtfelder — nur ein laufendes Speichern bremst.
        if (saving) return;
        setSaving(true);
        setError(null);
        try {
            const flowState: CreateFlowState = {
                betreff,
                vorgangsart: effektiveArt,
                slots,
                hv,
                bearbeiter,
                zuInformierende,
                twv,
                frist,
                twvZielgruppe: initialTwvZielgruppe,
                ersteller: currentUserId,
                linkedEntityType,
                linkedEntityId,
                persoenlich,
            };
            let result: VorgangCreateResult;
            const dokumentName = geaenderterEintragDokumentName();
            const dokumentNameFeld = dokumentName ? { dokumentName } : {};

            /**
             * Die Entwuerfe aus dem To-do-Block anlegen. Seit PO 14.09.2026
             * gilt das fuer BEIDE Wege: beim neuen Vorgang wie bisher — und
             * beim Zuweisen an einen bestehenden Vorgang neben dem To-do, das
             * aus dem Formular selbst entsteht. Vorher verschwanden Entwuerfe
             * im Zuweisungs-Zweig wortlos.
             */
            const legeDraftTodosAn = async (vorgangId: number) => {
                // T-39 (PO 01.10.2026): Ein Entwurf ohne Betreff wird ANGELEGT
                // (Ersatz-Titel aus der Beschreibung, sonst „To-do"). Bis dahin
                // fiel er hier wortlos weg — „Vorgang erstellen" meldete Erfolg,
                // die Eingabe war verloren. Nur eine völlig unberührte Karte
                // entfällt, und das sagt ein Hinweis.
                const vorgabe = currentUserId ? [currentUserId] : [];
                let leer = 0;
                for (const d of todoDrafts) {
                    if (todoDraftIstLeer(d, vorgabe)) {
                        leer++;
                        continue;
                    }
                    await vorgangService.createTodo(
                        buildDraftTodoPayload(d, { persoenlich, linkedEntityType, linkedEntityId }, vorgangId, currentUserId) as any,
                    );
                }
                if (leer > 0) {
                    zeigeGlobalenHinweis(
                        leer === 1
                            ? "1 leeres To-do wurde nicht angelegt — es hatte weder Betreff noch Inhalt."
                            : `${leer} leere To-dos wurden nicht angelegt — sie hatten weder Betreff noch Inhalt.`,
                        { art: "info" },
                    );
                }
            };

            if (targetVorgang) {
                // Einem bestehenden Vorgang zuweisen → das Erstellte ist ein Todo.
                const todo = await vorgangService.createTodo(
                    buildTodoInVorgangPayload(flowState, targetVorgang.id) as any,
                );
                if (notiz.text.trim()) {
                    await noteService.create(todo.id, { text: notiz.text.trim(), mentions: notiz.mentions });
                }
                await legeDraftTodosAn(targetVorgang.id);
                // K4: die Todo-Id bleibt (Mail-Weg wertet sie aus); zusätzlich der
                // Eltern-Vorgang, damit der Termin-Bind IHN verknüpfen kann.
                result = { todoId: todo.id, parentVorgangId: targetVorgang.id, ...dokumentNameFeld };
                // Anhänge VOR onCreated importieren: der Mail-Flow archiviert in
                // onCreated die Mail (Graph-Move ⇒ NEUE messageId) — danach wären
                // die Anhang-messageIds tot. onCreated wiederum MUSS vor navigate
                // fertig sein (MailLink-Remap), sonst lädt die Detailseite die
                // Links noch mit der alten Id („Mail nicht verfügbar" bis Reload).
                // Ohne Kunden landen die Anhänge am Vorgang (PO 14.09.2026:
                // persönlicher Vorgang aus einer Mail).
                await importSelectedAttachments(slots.kontakt?.id ?? null, targetVorgang.id);
                // Eigene Dateien haengen am erstellten To-do, nicht am fremden
                // Eltern-Vorgang — dort gehoert das Hochgeladene inhaltlich hin.
                await ladeDateienHoch({ todoId: todo.id });
                await onCreated?.(result);
                // Erst JETZT, da alles am To-do hängt: Trägt der Vorgang danach
                // genau ein To-do, wird es der Vorgang (User 03.11.2026).
                await stelleTodoFormHerStill(targetVorgang.id);
                closeWindow(windowId);
                navigate(`/vorgaenge/${targetVorgang.id}`);
            } else {
                const created = await vorgangService.createVorgang(buildVorgangPayload(flowState) as any);
                let linkedTodoId: number | undefined;
                // Vorgänge haben keinen generischen linkedEntity-Slot. Ein
                // objektbezogener Flow legt daher ein echtes Todo im neuen
                // Vorgang an, damit der Vorgang im Objekt-Feed sichtbar wird.
                // Ein persönlicher Vorgang hat keinen Objektbezug (und keine
                // Mail-Anhänge) — der Todo-Wrapper-Zweig entfällt dort.
                if (!persoenlich && linkedEntityType && linkedEntityId != null) {
                    const linkedTodo = await vorgangService.createTodo(
                        buildTodoInVorgangPayload(flowState, created.id) as any,
                    );
                    linkedTodoId = linkedTodo.id;
                }
                if (notiz.text.trim()) {
                    await vorgangNoteService.create(created.id, {
                        text: notiz.text.trim(),
                        mentions: notiz.mentions,
                    });
                }
                await legeDraftTodosAn(created.id);
                result = { vorgangId: created.id, todoId: linkedTodoId, ...dokumentNameFeld };
                // Reihenfolge wie im Todo-Zweig: Anhänge (alte messageIds) →
                // onCreated (Link + Archiv/Remap, awaited) → Navigation.
                // Ohne Kunden landen die Anhänge am Vorgang (PO 14.09.2026:
                // persönlicher Vorgang aus einer Mail).
                await importSelectedAttachments(slots.kontakt?.id ?? null, created.id);
                await ladeDateienHoch({ vorgangId: created.id });
                await onCreated?.(result);
                // Ein Vorgang mit genau einem To-do (Objekt-Bezug ODER ein
                // einzelner Entwurf) wird ein Vorgang ohne To-dos (User
                // 03.11.2026) — nach dem Anhängen, siehe oben.
                await stelleTodoFormHerStill(created.id);
                closeWindow(windowId);
                navigate(`/vorgaenge/${created.id}`);
            }
        } catch (err: any) {
            console.error("[VorgangCreateFlow] Anlegen fehlgeschlagen", err);
            setError(err?.message ?? "Vorgang konnte nicht angelegt werden.");
        } finally {
            setSaving(false);
        }
    }, [
        slots, saving, twv, frist, targetVorgang, betreff, hv,
        bearbeiter, zuInformierende, notiz, todoDrafts, effektiveArt, currentUserId, onCreated,
        closeWindow, windowId, navigate,
        // SP5/SP6: frische Anhang-Auswahl + Namen im save-Closure (importSelectedAttachments).
        mailAttachments, selectedAttKeys, attNames,
        // T-397: frischer Name des Kontakthistorie-PDFs im save-Closure.
        eintragDokument, eintragDokumentStamm,
        // PO 14.09.2026: frisch ausgewaehlte Dateien im save-Closure.
        dateien,
        linkedEntityType, linkedEntityId, initialTwvZielgruppe,
        persoenlich,
    ]);

    // ── E4: Die drei umsortierbaren Bloecke ────────────────────────────────
    // EINE Definition, zwei Reihenfolgen. Im einschrittigen Mail-Weg gilt die
    // PO-Reihenfolge (Notiz → TWV/Frist → Verantwortliche → Rest); im
    // zweischrittigen Modul-Flow bleibt die HEUTIGE Reihenfolge unveraendert
    // (Annahme A3 — der PO hat den Umbau ausdruecklich auf den Mail-Weg
    // begrenzt). Wuerden die Bloecke doppelt im JSX stehen, drifteten sie.
    const anhaengeBlock = (
        <>
            {/* SP5 (Entscheidung 31): Anhänge aus der E-Mail — vorausgewählt, abwählbar. */}
            {mailAttachments && mailAttachments.items.length > 0 && (
                <div>
                    <div style={{ fontWeight: 800, fontSize: "0.95rem", marginBottom: 6 }}>
                        Anhänge aus der E-Mail
                    </div>
                    <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", marginBottom: 8 }}>
                        {slots.kontakt
                            ? "Werden beim Kontakt als Dokumente gespeichert und dem Vorgang zugeordnet."
                            : "Werden als Dokumente am Vorgang gespeichert."}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {mailAttachments.items.map((a, i, alle) => {
                            const key = attKey(a);
                            // T-236: Überschrift vor dem ersten Bild aus dem Mailtext.
                            const ersteBildzeile = !!a.inline && !alle[i - 1]?.inline;
                            const sizeLabel = formatBytes(a.size);
                            const selected = selectedAttKeys.has(key);
                            const { base: origBase, ext } = splitAttName(a.name);
                            return (
                                <React.Fragment key={key}>
                                {ersteBildzeile && (
                                    <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", fontWeight: 700, marginTop: 4 }}>
                                        Bilder aus dem Mailtext — bei Bedarf anhaken
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        fontSize: "0.85rem",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleAtt(key)}
                                        style={{ flex: "0 0 auto", cursor: "pointer" }}
                                        aria-label={`Anhang „${a.name}" übernehmen`}
                                    />
                                    {/* Bilder aus dem Mailtext heißen alle „Bild aus Mail …" —
                                        erst die Vorschau sagt, was drauf ist. */}
                                    {a.inline && (
                                        <MailBildVorschau
                                            name={a.name}
                                            laden={() =>
                                                graphMailService.fetchAttachmentBlob(
                                                    mailAttachments.mailbox,
                                                    a.messageId,
                                                    a.attachmentId,
                                                )
                                            }
                                        />
                                    )}
                                    {selected ? (
                                        <>
                                            {/* Editierbarer Basename; Extension als fixer Suffix. */}
                                            <input
                                                className="vgd-input"
                                                style={{ flex: "1 1 auto", minWidth: 0, padding: "5px 9px", fontSize: "0.82rem" }}
                                                value={attNames[key] ?? origBase}
                                                onChange={(e) => setAttName(key, e.target.value)}
                                                placeholder={origBase}
                                                aria-label="Dateiname"
                                            />
                                            {ext && (
                                                <span style={{ flex: "0 0 auto", color: "var(--vgd-ink-3)", fontWeight: 600 }}>
                                                    {ext}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span
                                            onClick={() => toggleAtt(key)}
                                            style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 600, wordBreak: "break-all", cursor: "pointer", color: "var(--vgd-ink-3)" }}
                                        >
                                            {a.name}
                                        </span>
                                    )}
                                    {sizeLabel && (
                                        <span style={{ flex: "0 0 auto", color: "var(--vgd-ink-3)" }}>· {sizeLabel}</span>
                                    )}
                                </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* PO 14.09.2026: eigene Dateien schon beim Anlegen mitgeben.
                Hochgeladen wird erst NACH dem Anlegen — vorher gibt es keine
                Id, an der ein Dokument haengen koennte. */}
            <div>
                <div style={{ fontWeight: 800, fontSize: "0.95rem", marginBottom: 6 }}>Dokumente</div>
                <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", marginBottom: 8 }}>
                    {slots.kontakt
                        ? "Werden beim Kontakt als Dokumente gespeichert und dem Vorgang zugeordnet."
                        : "Werden am Vorgang gespeichert."}
                </div>
                {/* T-397: das PDF der Kontakthistorie — noch kein Dokument, aber der Name lässt sich schon ändern. */}
                {eintragDokument && (
                    <div style={{ marginBottom: 10 }}>
                        <DokumentNameFeld
                            original={eintragDokument.name}
                            stamm={eintragDokumentStamm}
                            onStamm={setEintragDokumentStamm}
                        />
                    </div>
                )}
                <input
                    ref={dateiInputRef}
                    type="file"
                    multiple
                    aria-label="Dateien auswählen"
                    style={{ display: "none" }}
                    onChange={(e) => dateienWaehlen(e.target.files)}
                />
                <button
                    type="button"
                    className="vgd-btn"
                    style={{ alignSelf: "flex-start" }}
                    onClick={() => dateiInputRef.current?.click()}
                >
                    <Add20Regular /> Dateien auswählen
                </button>
                {dateien.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                        {dateien.map((f, i) => (
                            <div
                                key={`${f.name}-${f.size}-${i}`}
                                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}
                            >
                                <span style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 600, wordBreak: "break-all" }}>
                                    {f.name}
                                </span>
                                {formatBytes(f.size) && (
                                    <span style={{ flex: "0 0 auto", color: "var(--vgd-ink-3)" }}>
                                        · {formatBytes(f.size)}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="vgd-btn"
                                    style={{ flex: "0 0 auto", padding: "2px 8px" }}
                                    aria-label={`${f.name} entfernen`}
                                    onClick={() => setDateien((prev) => prev.filter((_, idx) => idx !== i))}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );

    const beteiligteBlock = (
        <div>
            <div style={{ fontWeight: 800, fontSize: "0.95rem", marginBottom: 8 }}>
                Wer sind die Beteiligten?
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div className="vgd-field">
                    <span className="vgd-label">Kontaktverantwortlichkeit</span>
                    <span className="vgd-value">
                        {persoenlich ? (
                            <span className="vgd-persoenlich-badge">Persönlich</span>
                        ) : (
                            <span className="vgd-kvpill">{kvText}</span>
                        )}
                    </span>
                </div>
                {/* I1/E07: In einem persönlichen Vorgang bist NUR du beteiligt —
                    read-only gezeigt, spiegelt die Backend-Regel aus setBeteiligte. */}
                <div className="vgd-field">
                    <span className="vgd-label">Hauptverantwortlicher</span>
                    {persoenlich ? (
                        <span className="vgd-value">
                            <span className="vgd-minibadge">
                                {currentUserId ? kuerzel(currentUserId) : "—"}
                            </span>
                        </span>
                    ) : (
                        <UserMultiPicker value={hv} onChange={setHv} chip="kuerzel" placeholder="Person wählen…" />
                    )}
                </div>
                <div className="vgd-field">
                    <span className="vgd-label">Bearbeiter / weitere Bearbeiter</span>
                    {persoenlich ? (
                        <span className="vgd-value" style={{ color: "var(--vgd-ink-3)", fontSize: "0.82rem" }}>
                            In einem persönlichen Vorgang bist nur du Bearbeiter.
                        </span>
                    ) : (
                        <UserMultiPicker value={bearbeiter} onChange={setBearbeiter} placeholder="Personen wählen…" />
                    )}
                </div>
                {/* Zu Informierende stehen direkt im Block (PO 04.09.2026) —
                    der Ausklapper „weitere Optionen" enthielt nichts sonst und
                    ist ganz entfallen.

                    Bei „persönlich" entfällt das Feld: es gibt niemanden zu
                    informieren. Das Backend lehnt es nicht ab, es ÜBERSCHREIBT
                    still (`vorgang.controller.ts` setzt `zuInformierende: []`)
                    — die Sperre hier ist also die einzige Rückmeldung. */}
                {!persoenlich && (
                    <div className="vgd-field">
                        <span className="vgd-label">Zu informierende Personen</span>
                        <UserMultiPicker value={zuInformierende} onChange={setZuInformierende} placeholder="Personen wählen…" />
                    </div>
                )}
            </div>
        </div>
    );

    const termineBlock = (
        <div>
            <div style={{ fontWeight: 800, fontSize: "0.95rem", marginBottom: 8 }}>Termine</div>
            {/* C7: TWV und Frist IMMER beide, unabhängig setzbar. */}
            <div className="vgd-rowgrid" style={{ gridTemplateColumns: "repeat(2, minmax(140px, 1fr))" }}>
                <div className="vgd-field">
                    <span className="vgd-label" title="Termin-Wiedervorlage (dein Reminder)">
                        TWV
                    </span>
                    <input
                        type="date"
                        className="vgd-input"
                        value={twv ?? ""}
                        onChange={(e) => setTwv(e.target.value || null)}
                    />
                </div>
                <div className="vgd-field">
                    <span className="vgd-label">Frist</span>
                    <input
                        type="date"
                        className="vgd-input"
                        value={frist ?? ""}
                        onChange={(e) => setFrist(e.target.value || null)}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="vgd vgc-anlage" style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff" }}>
            {/* Seit 01.09.2026 EIN Formular statt zweier Schritte — die
                Schritt-Anzeige entfällt damit. Der Mail-Weg war ohnehin schon
                einschrittig (E1); jetzt gilt das für alle Aufrufer. */}

            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Zuweisung: im Mail-Weg steht der Kontakt schon fest und die
                    Karte erscheint stattdessen oben im Formular (E1). */}
                {!startetEinschrittig && (
                    <>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: "1.05rem", marginBottom: 4 }}>
                                Woran möchtest du arbeiten?
                            </div>
                            <div style={{ color: "var(--vgd-ink-2)", fontSize: "0.88rem" }}>
                                {persoenlich
                                    ? "Ein persönlicher Vorgang gehört nur dir — ohne Kontakt, Risiko oder Vertrag."
                                    : "Wähle einen Kontakt, wenn es einen gibt — er lässt sich auch später im Vorgang nachtragen oder ändern."}
                            </div>
                        </div>
                        {/* I1: Art der Zuweisung — native Radios im kvp-rd/vgd-Design. */}
                        {persoenlichMoeglich && (
                            <div className="vgd-field" role="radiogroup" aria-label="Art der Zuweisung">
                                <label className="vgd-radio">
                                    <input
                                        type="radio"
                                        name="vgd-zuweisungsart"
                                        checked={!persoenlich}
                                        onChange={() => wechsleZuweisungsart(false)}
                                    />
                                    <span>Vorgang für einen Kontakt</span>
                                </label>
                                <label className="vgd-radio">
                                    <input
                                        type="radio"
                                        name="vgd-zuweisungsart"
                                        checked={persoenlich}
                                        onChange={() => wechsleZuweisungsart(true)}
                                    />
                                    <span>Persönlicher Vorgang</span>
                                    <span className="vgd-radio-hint">nur für dich sichtbar</span>
                                </label>
                            </div>
                        )}
                        {/* Zuweisungs-Karte (C2): lila Balken links, K/R/V untereinander.
                            Bei „persönlich" verschwindet sie komplett (I1). */}
                        {!persoenlich && (
                            <div className="vgd-assign-card">
                                <LinkRow
                                    slots={slots}
                                    editable
                                    vertical
                                    onApply={(result: CascadeResult) => setSlots(result.slots)}
                                />
                            </div>
                        )}
                        {vorgangFuerBlock}
                        {/* Bei „persönlich" ist die Art festgelegt und wird
                            read-only gezeigt — wie Hauptverantwortlicher und
                            Bearbeiter darüber. Bewusst KEIN `<input>`: die
                            Betreff-Tests finden das Feld über das erste
                            `input.vgd-input`. */}
                        <div className="vgd-field">
                            <span className="vgd-label">Vorgangsart</span>
                            {persoenlich ? (
                                <span className="vgd-value">{effektiveArt}</span>
                            ) : (
                                <select
                                    className="vgd-input"
                                    value={effektiveArt}
                                    onChange={(e) => setVorgangsart(e.target.value)}
                                >
                                    {VORGANG_ARTEN.map((a) => (
                                        <option key={a} value={a}>
                                            {a}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </>
                )}
                <>
                        {/* E1/PO-Blatt 2: Im Mail-Weg steht die Zuweisung OBEN im Formular —
                            Kontaktname und darunter seine Standard-Anschrift. Sie bleibt
                            änderbar; Risiko/Vertrag zeigt die Karte hier nicht (P2). */}
                        {startetEinschrittig && !persoenlich && (
                            <div className="vgd-assign-card">
                                <LinkRow
                                    slots={slots}
                                    editable
                                    vertical
                                    zeigeKontaktAdresse
                                    onApply={(result: CascadeResult) => setSlots(result.slots)}
                                />
                            </div>
                        )}
                        {startetEinschrittig && vorgangFuerBlock}
                        {/* E2: Im Mail-Weg stehen Vorgangsart und Betreff in EINER Zeile.
                            Der Art-Select wird dabei aus Schritt 1 VERSCHOBEN, nicht
                            dupliziert — sonst gäbe es zwei Wahrheiten für dieselbe Art. */}
                        <div
                            className="vgd-rowgrid"
                            style={
                                startetEinschrittig
                                    ? { gridTemplateColumns: "150px minmax(0,1fr)" }
                                    : { gridTemplateColumns: "minmax(0,1fr)" }
                            }
                        >
                            {startetEinschrittig && (
                                <div className="vgd-field">
                                    <span className="vgd-label">Vorgangsart</span>
                                    {/* Gleiche Regel wie in Schritt 1, auch
                                        wenn „persönlich" im Mail-Weg heute
                                        unerreichbar ist: die Invariante darf
                                        nicht an einer Aufrufer-Zufälligkeit
                                        hängen. */}
                                    {persoenlich ? (
                                        <span className="vgd-value">{effektiveArt}</span>
                                    ) : (
                                        <select
                                            className="vgd-input"
                                            value={effektiveArt}
                                            onChange={(e) => setVorgangsart(e.target.value)}
                                        >
                                            {VORGANG_ARTEN.map((a) => (
                                                <option key={a} value={a}>
                                                    {a}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}
                            <div className="vgd-field">
                                <span className="vgd-label">Betreff (optional)</span>
                                {/* PO 28.09.2026: der Mail-Betreff als Pille IM Feld,
                                    übernommen als Text im Feld (T-47) — siehe BetreffFeld.
                                    T-46: ohne führenden Namen des zugewiesenen Kontakts. */}
                                <BetreffFeld
                                    value={betreff}
                                    onChange={setBetreff}
                                    autoFocus={!betreff}
                                    mailBetreff={mailBetreff}
                                    kontaktNamen={kontaktNamen}
                                    placeholder={
                                        betreffOptional
                                            ? `Worum geht es? Ohne Angabe steht „${effektiveArt}" als Betreff`
                                            : 'Worum geht es? (z. B. „Neues Kfz-Risiko für Firma X erfassen")'
                                    }
                                />
                            </div>
                        </div>

                        <div className="vgd-field">
                            <span className="vgd-label">Notiz/Beschreibung</span>
                            <MentionTextarea
                                value={notiz.text}
                                onChange={setNotiz}
                                placeholder="Kontext für den Aufgaben-Chat… mit @ jemanden erwähnen"
                                minRows={3}
                            />
                            <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", marginTop: 4 }}>
                                Wird beim Erstellen als erste Notiz im Aufgaben-Chat gepostet.
                            </div>
                        </div>

                        {/* E4: Reihenfolge je Modus — siehe die Block-Konstanten oben. */}
                        {startetEinschrittig ? (
                            <>
                                {termineBlock}
                                {beteiligteBlock}
                                {anhaengeBlock}
                            </>
                        ) : (
                            <>
                                {anhaengeBlock}
                                {beteiligteBlock}
                                {termineBlock}
                            </>
                        )}

                        {/* Zuweisen und To-dos stehen NEBENEINANDER (PO 14.09.2026):
                            wer einem bestehenden Vorgang zuweist, legt dort ein To-do an
                            — und darf gleich weitere daneben stellen. */}
                        {/* I1: „Einem bestehenden Vorgang zuweisen" entfällt bei einem
                            persönlichen Vorgang — er entsteht immer neu. */}
                        {!persoenlich && (
                            <div className="vgd-field">
                                <span className="vgd-label">Einem bestehenden Vorgang zuweisen (optional)</span>
                                <VorgangZuweisenPicker
                                    value={targetVorgang}
                                    onChange={setTargetVorgang}
                                    contactId={slots.kontakt?.id ?? null}
                                />
                                {targetVorgang && (
                                    <div style={{ color: "var(--vgd-ink-3)", fontSize: "0.75rem", marginTop: 4 }}>
                                        Das Erstellte wird ein Todo in diesem Vorgang.
                                    </div>
                                )}
                            </div>
                        )}
                        <div>
                            <div style={{ fontWeight: 800, fontSize: "0.95rem", marginBottom: 8 }}>
                                {targetVorgang ? "Weitere To-dos" : "Todos"}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {todoDrafts.map((d) => (
                                    <TodoDraftCard
                                        key={d.key}
                                        draft={d}
                                        persoenlich={persoenlich}
                                        onChange={(next) =>
                                            setTodoDrafts((prev) => prev.map((x) => (x.key === d.key ? next : x)))
                                        }
                                        onRemove={() => setTodoDrafts((prev) => prev.filter((x) => x.key !== d.key))}
                                    />
                                ))}
                                {/* „Weiteres" erst, wenn schon eines steht
                                    (PO 04.09.2026) — bei leerer Liste wäre
                                    es falsch. Mit Ziel-Vorgang ist das
                                    Erstellte selbst schon ein To-do, also
                                    heißt es dort von Anfang an „weiteres".
                                    Schreibweise „To-do" wie in beiden
                                    Detailansichten. */}
                                <button type="button" className="vgd-btn" style={{ alignSelf: "flex-start" }} onClick={addDraft}>
                                    <Add20Regular />{" "}
                                    {targetVorgang || todoDrafts.length > 0
                                        ? "Weiteres To-do hinzufügen"
                                        : "To-do hinzufügen"}
                                </button>
                            </div>
                        </div>
                </>
                {error && <div style={{ color: "#c23b3b", fontSize: "0.88rem" }}>{error}</div>}
            </div>

            {/* Fuß-Leiste */}
            <div style={{ display: "flex", gap: 10, padding: "10px 16px", borderTop: "1px solid var(--vgd-line)", alignItems: "center", flexWrap: "wrap" }}>
                {persoenlich && (
                    <span style={{ color: "var(--vgd-ink-2)", fontSize: "0.85rem" }}>
                        <strong>Persönlicher Vorgang</strong>
                    </span>
                )}
                {!persoenlich && slots.kontakt && !startetEinschrittig && (
                    <span style={{ color: "var(--vgd-ink-2)", fontSize: "0.85rem" }}>
                        Zuweisung: <strong>{kontaktLabel(slots.kontakt)}</strong>
                    </span>
                )}
                {/* Seit PO 28.09.2026 gibt es keine Sackgassen mehr: ohne Kontakt
                    entsteht ein Vorgang ohne Kunden, ohne Betreff einer mit leerem
                    bzw. Kürzel-Betreff. Die früheren roten Hinweise sind entfallen. */}
                <span style={{ flex: 1 }} />
                <button type="button" className="vgd-btn" onClick={() => closeWindow(windowId)} disabled={saving}>
                    Abbrechen
                </button>
                {/* EIN Knopf: Seit die beiden Schritte zusammenliegen, muss er
                    BEIDE Bedingungen prüfen — ohne Kontakt (bzw. „persönlich")
                    weist `POST /vorgang` ab, ohne Betreff bricht `save()` ab.
                    Woran es jeweils fehlt, sagen die Hinweise links daneben. */}
                <button
                    type="button"
                    className="vgd-primarybtn"
                    style={{ alignSelf: "auto" }}
                    disabled={saving}
                    onClick={() => void save()}
                >
                    {saving ? "Wird angelegt…" : targetVorgang ? "Todo im Vorgang anlegen" : "Vorgang erstellen"}
                </button>
            </div>
        </div>
    );
};

/**
 * Öffnet den zweistufigen Erstell-Flow als Light-Popup (SP3, C1): schmal,
 * IMMER zentriert (center), verschiebbar/minimierbar/schließbar über
 * FloatingWindow; auf dem Phone Vollbild (WindowLayer).
 */
export const openVorgangCreateFlow = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenVorgangCreateFlowParams = {},
): string => {
    const windowId = `vorgang-create-${Date.now()}`;
    openWindow({
        id: windowId,
        title: "Neuer Vorgang",
        icon: React.createElement(Add20Regular),
        initialSize: { width: 520, height: 640 },
        center: true,
        render: () => React.createElement(VorgangCreateFlowWindow, { windowId, ...params }),
    });
    return windowId;
};

export default VorgangCreateFlowWindow;
